// src/services/authService.ts
import axios from 'axios';
import { API_BASE_URL, API_PATH } from '../config/api.config';

interface LoginRequest {
  username: string;
  password: string;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  user: {
    username: string;
    email: string;
    full_name: string;
    disabled: boolean;
    role: string;
    default_app: string | null;
  };
}

// Segéd metódus a JWT dekódoláshoz
function parseJwt(token: string) {
  try {
    // JWT token 3 részből áll: header.payload.signature
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return null;
  }
}

class AuthService {
  private baseUrl: string;
  private token?: string | null;
  private tokenRefreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.baseUrl = `${API_BASE_URL}${API_PATH}`;
  }

// src/services/authService.ts - authService osztály kibővítése

async generateAppToken(): Promise<boolean> {
  try {
    const response = await axios.post(`${this.baseUrl}/auth/generate-app-token`, {}, {
      headers: this.getAuthHeader(),
      withCredentials: true // Ez biztosítja, hogy a cookie-kat megfelelően kezeljük
    });
    
    if (response.data && response.data.status === 'success') {
      console.log('App token successfully generated');
      return true;
    } else {
      console.warn('Unexpected response from generate-app-token:', response.data);
      return false;
    }
  } catch (error) {
    console.error('Error generating app token:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Server response:', error.response.data);
      throw new Error(`Failed to generate app token: ${error.response.data.detail || 'Unknown error'}`);
    }
    throw new Error('Failed to generate app token');
  }
}


  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      console.log('Sending login request to:', `${this.baseUrl}/auth/login`);
      const response = await axios.post<AuthResponse>(`${this.baseUrl}/auth/login`, credentials, {
        withCredentials: true 
      });
      
      const { access_token, user } = response.data;
      
      if (!access_token) {
        throw new Error('Token is missing in the response');
      }
      
      this.token = access_token;
      localStorage.setItem('testpulse_token', access_token);
      localStorage.setItem('testpulse_user', JSON.stringify(user));
  
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Authentication failed. Please check your credentials.');
    }
  }

  logout(): void {
    localStorage.removeItem('testpulse_token');
    localStorage.removeItem('testpulse_user');
    this.token = null;
  }

  getToken(): string | null {
    return localStorage.getItem('testpulse_token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Ellenőrizzük, hogy a token nem járt-e le
    try {
      const tokenData = parseJwt(token);
      if (!tokenData) return false;
      
      // Token lejáratának ellenőrzése
      const expirationTime = tokenData.exp * 1000; // ms-ben
      return Date.now() < expirationTime;
    } catch (e) {
      console.error('Error checking token validity:', e);
      return false;
    }
  }
  
  getCurrentUser(): unknown {
    const user = localStorage.getItem('testpulse_user');
    return user ? JSON.parse(user) : null;
  }
  
  // Ezt a metódust használjuk a többi szolgáltatásban, hogy biztosítsuk
  // minden API kérés tartalmazza a Bearer tokent
  getAuthHeader(): { Authorization: string } | Record<string, never> {
    const token = this.getToken();
    
    if (token) {
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  }

  // Token lejárati idejének ellenőrzése
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;
    
    try {
      const tokenData = parseJwt(token);
      if (!tokenData) return true;
      
      const expirationTime = tokenData.exp * 1000; // ms-ben
      
      // Biztonságos időhatár: 5 perccel a lejárat előtt már frissítsünk
      const safetyBuffer = 5 * 60 * 1000; // 5 perc milliszekundumban
      return Date.now() >= (expirationTime - safetyBuffer);
    } catch (e) {
      console.error('Error checking token expiration:', e);
      return true;
    }
  }

  // Token frissítése ha szükséges
  async refreshTokenIfNeeded(): Promise<boolean> {
    // Ha a token nem járt le, nincs teendő
    if (!this.isTokenExpired()) {
      return true;
    }
    
    // Ha már fut egy token frissítés, akkor azt a Promise-t adjuk vissza
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }
    
    // Új token frissítés indítása
    this.tokenRefreshPromise = this.doRefreshToken();
    
    try {
      // Várakozás a token frissítésre
      const result = await this.tokenRefreshPromise;
      return result;
    } finally {
      // A Promise törlése, hogy következő alkalommal új frissítés indulhasson
      this.tokenRefreshPromise = null;
    }
  }





  // A tényleges token frissítés végrehajtása
  private async doRefreshToken(): Promise<boolean> {
    const token = this.getToken()
    // Ha nincs token, nem lehet frissíteni
    if (!token) {
      return false;
    }
    
    try {
      // Közvetlenül hozzuk létre az Authorization fejlécet a tokennel
      const headers = { Authorization: `Bearer ${this.token}` };
      
      const response = await axios.post(
        `${this.baseUrl}/auth/refresh-token`,
        {},
        { headers }
      );
      
      if (response.data && response.data.access_token) {
        this.token = response.data.access_token;
        localStorage.setItem('testpulse_token', this.token!);
        
        // Ha van user adat a válaszban, azt is frissítsük
        if (response.data.user) {
          localStorage.setItem('testpulse_user', JSON.stringify(response.data.user));
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Ha nem sikerül a frissítés, töröljük a tokent és kijelentkeztetünk
      this.logout();
      return false;
    }
  }

  // Token érvényességének ellenőrzése a szerveren
  async validateToken(): Promise<boolean> {
    if (!this.token) return false;
    
    try {
      // Itt is közvetlenül hozzuk létre a headers objektumot
      const headers = { Authorization: `Bearer ${this.token}` };
      
      const response = await axios.post(
        `${this.baseUrl}/auth/validate-token`,
        {},
        { headers }
      );
      
      return response.data && response.data.valid === true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }
}

export default new AuthService();